import { motion } from "framer-motion";
import { Reveal } from "../../lib/animations";
import { CONTACT, PHOTOS } from "../../lib/site";

const PARTNERS = ["InterContinental", "KLM", "Gemeente Amsterdam", "Mezrab"];

const PILLARS = [
  { t: "Beauty", d: "The power of presence and self-expression." },
  { t: "Learning", d: "International, creative and holistic workshops." },
  { t: "Movement", d: "Confidence and connection through the body." },
];

const IMAGES = [
  { src: PHOTOS.purpleStage, alt: "Cibele on stage in a purple gown", shape: "rounded-t-[110px]" },
  { src: PHOTOS.green, alt: "Cibele in an emerald and gold costume", shape: "rounded-b-[110px]" },
];

export const B2B = () => {
  return (
    <section
      id="b2b"
      data-testid="b2b-partners-section"
      className="relative bg-champagne px-6 py-28 md:px-12 md:py-40"
    >
      <div className="mx-auto max-w-[1600px]">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12 lg:gap-20">
          {/* Left: copy + pillars + CTA */}
          <Reveal className="lg:col-span-5">
            <p className="mb-6 flex items-center gap-3 font-sans text-[0.7rem] uppercase tracking-[0.34em] text-terracotta">
              <span className="h-px w-10 bg-gold" /> For Business
            </p>
            <h2 className="font-serif text-4xl font-light leading-[1.05] tracking-tight text-espresso md:text-6xl">
              Tailored workshops for <span className="italic text-plum">companies</span>.
            </h2>
            <p className="mt-8 max-w-md font-sans text-base leading-relaxed text-espresso/70">
              Built on a philosophy of the power of beauty and learning —
              internationally minded, creative and holistic experiences designed
              around your team.
            </p>

            <div className="mt-10 flex flex-col divide-y divide-espresso/15 border-y border-espresso/15">
              {PILLARS.map((p, i) => (
                <div key={p.t} className="flex items-baseline gap-5 py-5">
                  <span className="font-serif text-xl italic text-gold">0{i + 1}</span>
                  <div>
                    <h3 className="font-serif text-xl text-espresso">{p.t}</h3>
                    <p className="mt-1 font-sans text-sm leading-relaxed text-espresso/60">
                      {p.d}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <a
              href={`mailto:${CONTACT.email}`}
              data-testid="b2b-contact-btn"
              className="group mt-10 inline-flex items-center gap-3 rounded-full border border-espresso/30 px-8 py-4 font-sans text-xs uppercase tracking-[0.24em] text-espresso transition-colors duration-300 hover:bg-espresso hover:text-champagne"
            >
              {CONTACT.email}
              <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">→</span>
            </a>
          </Reveal>

          {/* Right: 2x2 staggered image collage */}
          <div className="lg:col-span-7">
            <div className="grid grid-cols-2 gap-4 md:gap-6">
              {IMAGES.map((img, i) => (
                <motion.figure
                  key={img.src}
                  data-testid={`b2b-image-${i}`}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1], delay: i * 0.08 }}
                  className={`group relative overflow-hidden border border-gold/25 ${img.shape} ${
                    i % 2 === 1 ? "md:mt-12" : ""
                  }`}
                >
                  <div className="aspect-[3/4] w-full overflow-hidden">
                    <img
                      src={img.src}
                      alt={img.alt}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.76,0,0.24,1)] group-hover:scale-110"
                    />
                  </div>
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-espresso/40 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                </motion.figure>
              ))}
            </div>
          </div>
        </div>

        {/* Partners */}
        <Reveal className="mt-28">
          <p className="mb-10 text-center font-sans text-[0.65rem] uppercase tracking-[0.4em] text-espresso/50">
            Trusted by & performed for
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-8 border-y border-espresso/15 py-10 md:gap-x-20">
            {PARTNERS.map((p) => (
              <span
                key={p}
                data-testid={`partner-${p.toLowerCase().replace(/\s+/g, "-")}`}
                className="font-serif text-2xl tracking-wide text-espresso/40 transition-colors duration-300 hover:text-plum md:text-3xl"
              >
                {p}
              </span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
